import * as React from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  Activity,
  Bell,
  Database,
  Folder,
  GitBranch,
  Home,
  LayoutGrid,
  ListChecks,
  MessageSquare,
  Plus,
  ScrollText,
  Settings as SettingsIcon,
  ShieldCheck,
  Target,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useAuth } from '@/lib/auth/store';
import { usePortfolioQuery } from '@/lib/api/portfolio';
import { useClientsQuery } from '@/lib/api/clients';
import { useNotificationsInboxQuery } from '@/lib/api/notifications';
import { type PortfolioPosture, type PortfolioEngagementDto } from '@/lib/api/portfolio';
import { useSelectedClient } from '@/lib/stores/selected-client';
import { IVV_MODULES } from '@/lib/engagement-modules';
import { avatarTone, deriveInitials } from '@/components/clients/team-avatars';

/**
 * Top-level left sidebar (C20-129 / E10 fix).
 *
 * Mirrors `design_handoff_caliber_v2/prototype/shell.js → renderSidebar()`
 * and the `.sidebar / .nav-item / .sidebar-section / .num / .brand-stack`
 * rules in `prototype/tokens.css`. Replaces the T-0096 IA-tracking shell
 * with the prototype's full structural shape:
 *
 *   - Brand block: avero-logo.png + wordmark stack (firm name + Caliber 2.0)
 *   - Top-level nav: Portfolio / Clients / Notifications — each with a
 *     live count badge from the matching backend query
 *   - Engagements section header (uppercase tracked muted) + the
 *     engagement list scoped to ONE client — the selected client (see
 *     `lib/stores/selected-client.ts`). Listing every client's engagements
 *     here duplicated the Clients page and got noisy; the Clients page is
 *     where you switch. A client-name row above the list says which client
 *     is in scope and links to its detail page. Each engagement row shows
 *     a posture-colored dot + engagement name; clicking navigates into it.
 *   - Active engagement expands inline beneath its row with its sub-items
 *     (Overview / Contract Compliance / Health & Readiness / Registers /
 *     Documents / Chat / Snapshots / Audit trail) connected by a left
 *     rule. The active sub-item picks up the brand accent. The module
 *     items are listed by the engagement's module flags (see
 *     `engagementSubItems`).
 *   - mt-auto spacer + Component library + Firm settings at the bottom
 *     (matches the prototype's "secondary affordances after the spacer"
 *     placement).
 *   - User card at the very bottom: deterministic avatar palette tone
 *     from the user id + name + role hint, top border.
 *
 * Data sources:
 *   - `usePortfolioQuery()` — engagement list (with posture rollup) +
 *     the count badge for "Portfolio".
 *   - `useClientsQuery()` — count badge for "Clients".
 *   - `useNotificationsInboxQuery()` — unread count badge for "Notifications".
 *   - `useAuth().identity` — name/email for the user card.
 *
 * Firm name: hardcoded to "Avero Advisors" — single-firm v1, no firm
 * resolver endpoint yet. Documented in CLAUDE.md §What this codebase is.
 *
 * Required reading: design_handoff_caliber_v2/README.md §4, §5; the
 * prototype's `shell.js → renderSidebar` is the visual contract.
 */

const FIRM_NAME = 'Avero Advisors';
const PRODUCT_NAME = 'Caliber 2.0';

// Sub-tab tree for an active engagement. Order + labels mirror the
// prototype's `engagementNav` tabs array. `to` maps to a TanStack file
// route path under `/engagements/$engagementId/...`. The Registers item
// points at `/register` to match the existing scaffold route — the
// prototype calls the route `register` (singular) too.
interface EngagementSubItem {
  label: string;
  to: string;
  icon: typeof Home;
  /**
   * Module flags (`modules_enabled` values) the item belongs to, if any:
   * the item is listed when any of them is on.
   */
  modules?: ReadonlyArray<string>;
}

const ENGAGEMENT_SUB_ITEMS: EngagementSubItem[] = [
  { label: 'Overview', to: '', icon: Home },
  // Listed for any IV&V module, not only its own (see engagementSubItems).
  {
    label: 'Contract Compliance',
    to: 'contract/baseline',
    icon: ShieldCheck,
    modules: IVV_MODULES,
  },
  // E20: one screen for the health check and go-live readiness; the old
  // Health Check and Go-Live Readiness routes redirect to it (T-0224).
  {
    label: 'Health & Readiness',
    to: 'health-readiness',
    icon: Activity,
    modules: ['health_check', 'go_live_readiness'],
  },
  { label: 'Discovery', to: 'discovery', icon: Users, modules: ['discovery'] },
  { label: 'Selection', to: 'selection', icon: Target, modules: ['selection'] },
  // The registers hold risks and issues, which only the IV&V modules read
  // and write; nothing in Discovery or Selection points at them.
  { label: 'Registers', to: 'register', icon: ListChecks, modules: IVV_MODULES },
  { label: 'Documents', to: 'documents', icon: Folder },
  { label: 'Chat', to: 'chat', icon: MessageSquare },
  { label: 'Snapshots', to: 'snapshot', icon: GitBranch },
  { label: 'Audit trail', to: 'audit', icon: ScrollText },
];

/**
 * The sub-items an engagement shows.
 *
 * A module item is listed when any of its flags is on: Health & Readiness
 * when Health Check or Go-Live Readiness is (the screen serves either
 * alone), Discovery and Selection by their own flags (the API keeps
 * Selection on while Discovery is on, T-0171, so both appear together).
 * Contract Compliance and Registers belong to all three IV&V modules:
 * listed as soon as the engagement has any IV&V module, which is the
 * behaviour every existing engagement already has, and hidden only for one
 * with none (Discovery and/or Selection only, as the City of Everett on 22
 * October 2026), where they would lead nowhere: the registers hold risk and
 * issue entries that only the IV&V workers and screens read or write, and
 * the register route sends such an engagement to its overview. Hiding
 * Contract Compliance by its own flag is a wider change to existing
 * engagements and is deliberately not made here.
 *
 * The AI proposal review queue (`/proposals`) is never a sidebar item: it
 * is reached from the Overview's AI tile and the activity strip, and for an
 * engagement with no IV&V module its route redirects to Discovery's
 * findings review or to Selection (`lib/engagement-modules`).
 */
export function engagementSubItems(modulesEnabled: ReadonlyArray<string>): EngagementSubItem[] {
  const isOn = (flag: string) => modulesEnabled.includes(flag);
  return ENGAGEMENT_SUB_ITEMS.filter((item) => !item.modules || item.modules.some(isOn));
}

/**
 * Posture → CSS variable for the small posture dot rendered next to each
 * engagement row. Mirrors `prototype/shell.js → engagementNav` where the
 * dot color is `var(--posture-{e.posture})`. The portfolio composite
 * posture vocabulary differs from the chip vocabulary (green vs healthy)
 * so we project explicitly here.
 */
function posturePalette(posture: PortfolioPosture): string {
  switch (posture) {
    case 'green':
      return 'var(--posture-healthy)';
    case 'yellow_divergent':
      return 'var(--posture-at-risk)';
    case 'red':
      return 'var(--posture-critical)';
    case 'grey':
    case 'unassessed':
    default:
      return 'var(--posture-unassessed)';
  }
}

/**
 * Pull the active engagement id (if any) off the URL. TanStack's
 * `useParams({ from: '/engagements/$engagementId' })` throws on routes
 * that don't match — so on portfolio / clients / settings the call would
 * blow up. Reading the pathname directly works for every route the
 * sidebar mounts under.
 */
function useActiveEngagementId(): string | null {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/engagements\/([0-9a-f-]{36})(?:\/|$)/i);
  return match?.[1] ?? null;
}

/** Client id off a `/clients/:clientId` URL, same pathname approach as above. */
function useRouteClientId(): string | null {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/clients\/([0-9a-f-]{36})(?:\/|$)/i);
  return match?.[1] ?? null;
}

/**
 * Whether the build lists the component library (`/components`): dev builds
 * only. Read at render, not at import, so a test can switch the mode.
 */
export function showComponentLibrary(): boolean {
  return import.meta.env.DEV;
}

export function Sidebar() {
  const { pathname } = useLocation();
  const activeEngagementId = useActiveEngagementId();
  const routeClientId = useRouteClientId();

  const portfolioQuery = usePortfolioQuery();
  const clientsQuery = useClientsQuery();
  const notificationsQuery = useNotificationsInboxQuery();

  const engagementCount = portfolioQuery.data?.engagements.length ?? 0;
  const clientCount = clientsQuery.data?.length ?? 0;
  const unreadCount = notificationsQuery.data?.unread_count ?? 0;

  const allEngagements = React.useMemo<PortfolioEngagementDto[]>(
    () => portfolioQuery.data?.engagements ?? [],
    [portfolioQuery.data],
  );

  // Which client the Engagements section is scoped to. Precedence:
  //   1. the active engagement's client (deep links / portfolio cards
  //      always land with the right client in the sidebar),
  //   2. the client detail page currently open,
  //   3. the last selection, persisted across reloads.
  // 1 and 2 are written back into the store so navigating away keeps
  // that client in scope.
  const storedClientId = useSelectedClient((s) => s.selectedClientId);
  const setSelectedClientId = useSelectedClient((s) => s.setSelectedClientId);
  const activeEngagementClientId =
    allEngagements.find((e) => e.id === activeEngagementId)?.client_id ?? null;
  const selectedClientId = activeEngagementClientId ?? routeClientId ?? storedClientId;

  React.useEffect(() => {
    const next = activeEngagementClientId ?? routeClientId;
    if (next && next !== storedClientId) setSelectedClientId(next);
  }, [activeEngagementClientId, routeClientId, storedClientId, setSelectedClientId]);

  const engagements = React.useMemo(
    () => allEngagements.filter((e) => e.client_id === selectedClientId),
    [allEngagements, selectedClientId],
  );
  const selectedClientName =
    engagements[0]?.client_name ??
    clientsQuery.data?.find((c) => c.id === selectedClientId)?.name ??
    null;

  // Active rules mirror `prototype/shell.js → renderSidebar` — Portfolio is
  // only active on the bare `/` route; an active engagement lights up its
  // own inline row + sub-nav instead. The top-level Portfolio chip stays
  // dim so the user can see they're scoped inside a specific engagement.
  const isPortfolio = pathname === '/';
  const isClients = pathname === '/clients' || pathname.startsWith('/clients/');
  const isNotifications = pathname === '/notifications' || pathname.startsWith('/notifications/');
  const isComponents = pathname === '/components';
  const isSettings = pathname === '/settings' || pathname.startsWith('/settings/');

  return (
    <aside
      // `.sidebar` — `background: var(--paper-100); border-right`; `position:
      // sticky`; `padding: 16px 12px`; column gap 4px; height 100vh.
      // Adding `overflow-y-auto` because the engagement list + nested sub-
      // nav can exceed 100vh on a smaller screen and the prototype's
      // CSS allows scrolling inside the sticky sidebar.
      className="sticky top-0 flex h-screen w-[240px] flex-col gap-1 overflow-y-auto border-r border-ink-100 bg-paper-100 px-3 py-4"
      aria-label="Primary navigation"
    >
      {/* Brand block — matches `.brand` + `.brand-stack` in tokens.css. The
          avero-logo.png is served from /public so the relative href is
          stable across Vite dev + production builds. */}
      <Link
        to="/"
        className="brand mb-3 flex items-center gap-2 px-1 py-1.5 text-ink-900 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      >
        <img src="/avero-logo.png" alt={FIRM_NAME} className="brand-logo" />
        <span className="brand-stack">
          <span className="brand-name">{FIRM_NAME}</span>
          <span className="brand-product">{PRODUCT_NAME}</span>
        </span>
      </Link>

      <nav className="flex flex-col gap-1" aria-label="Sections">
        <TopNavItem
          to="/"
          icon={LayoutGrid}
          label="Portfolio"
          count={engagementCount}
          active={isPortfolio}
        />
        <TopNavItem
          to="/clients"
          icon={Users}
          label="Clients"
          count={clientCount}
          active={isClients}
        />
        <TopNavItem
          to="/notifications"
          icon={Bell}
          label="Notifications"
          count={unreadCount}
          active={isNotifications}
        />
      </nav>

      {/* Engagements section — matches `.sidebar-section` header + the
          inline engagement list from `prototype/shell.js → renderSidebar`. */}
      <div className="sidebar-section">Engagements</div>
      <div className="flex flex-col gap-0.5">
        {selectedClientId && selectedClientName ? (
          <>
            {/* Client-in-scope row: says which client the list below belongs
                to and links to its detail page. Switching clients happens
                on the Clients page (top nav), not here. */}
            <Link
              to="/clients/$clientId"
              params={{ clientId: selectedClientId }}
              className={cn(
                'mb-0.5 flex h-7 items-center gap-2 rounded-card px-2.5 text-meta font-semibold text-ink-900 no-underline transition-colors',
                'hover:bg-paper-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
              )}
              title={`${selectedClientName} — open client`}
            >
              <Users className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
              <span className="truncate">{selectedClientName}</span>
            </Link>
            {engagements.length === 0 && !portfolioQuery.isLoading ? (
              <p className="px-2.5 py-1 text-meta text-ink-500">No engagements yet.</p>
            ) : null}
            {engagements.map((engagement) => (
              <EngagementNavItem
                key={engagement.id}
                engagement={engagement}
                isActive={activeEngagementId === engagement.id}
                pathname={pathname}
              />
            ))}
          </>
        ) : clientsQuery.isLoading || portfolioQuery.isLoading ? null : (
          <p className="px-2.5 py-1 text-meta text-ink-500">
            Pick a client under{' '}
            <Link to="/clients" className="font-medium text-ink-700 underline">
              Clients
            </Link>{' '}
            to see its engagements here.
          </p>
        )}
        {/*
          New-engagement quick action — preserved from the T-0117 wiring.
          Stamped with `data-create="engagement"` so the global
          EditorModalHost captures the click and opens the wizard. The
          scoped client is passed along so the wizard opens preselected.
        */}
        <button
          type="button"
          data-create="engagement"
          data-preselect-client-id={
            selectedClientId && selectedClientName ? selectedClientId : undefined
          }
          className={cn(
            'mt-2 flex h-8 items-center gap-2.5 rounded-card border border-accent-amber-600 bg-accent-amber-600 px-2.5 text-body-sm font-medium text-paper-0',
            'transition-colors hover:bg-accent-amber-700',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-2',
          )}
        >
          <Plus className="h-4 w-4 flex-none" aria-hidden="true" />
          <span className="truncate">New engagement</span>
        </button>
      </div>

      {/* Spacer + secondary affordances. Prototype places Component library +
          Firm settings AFTER the mt-auto spacer; we match. The component
          library is an engineering reference, so only development builds
          list it (C2-40); production users never see it. */}
      <div className="mt-auto" />
      {showComponentLibrary() ? (
        <TopNavItem
          to="/components"
          icon={Database}
          label="Component library"
          active={isComponents}
        />
      ) : null}
      <TopNavItem to="/settings" icon={SettingsIcon} label="Firm settings" active={isSettings} />

      <UserCard />
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

interface TopNavItemProps {
  to: string;
  icon: typeof Home;
  label: string;
  active: boolean;
  count?: number;
}

function TopNavItem({ to, icon: Icon, label, active, count }: TopNavItemProps) {
  return (
    <Link
      to={to}
      className={cn(
        // Base `.nav-item` — h-8, px-2.5, gap-2.5, rounded, text-body-sm,
        // medium, ink-700; hover bg paper-200.
        'flex h-8 items-center gap-2.5 rounded-card px-2.5 text-body-sm font-medium text-ink-700 no-underline transition-colors',
        'hover:bg-paper-200 hover:text-ink-900',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        // Active rule: paper-0 bg, ink-100 border (`.nav-item.active` in
        // tokens.css). We additionally tint the leading icon with brand
        // accent to mirror `.nav-item.active svg`.
        active && 'border border-ink-100 bg-paper-0 text-ink-900',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <Icon
        className={cn('h-4 w-4 flex-none', active ? 'text-accent-amber-600' : 'text-ink-500')}
        aria-hidden="true"
      />
      <span className="truncate">{label}</span>
      {typeof count === 'number' && count > 0 ? <span className="nav-num">{count}</span> : null}
    </Link>
  );
}

interface EngagementNavItemProps {
  engagement: PortfolioEngagementDto;
  isActive: boolean;
  pathname: string;
}

function EngagementNavItem({ engagement, isActive, pathname }: EngagementNavItemProps) {
  const dotColor = posturePalette(engagement.posture);

  if (!isActive) {
    return (
      <Link
        to="/engagements/$engagementId"
        params={{ engagementId: engagement.id }}
        className={cn(
          'flex h-8 items-center gap-2 rounded-card px-2.5 text-body-sm font-medium text-ink-700 no-underline transition-colors',
          'hover:bg-paper-200 hover:text-ink-900',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        )}
        title={`${engagement.client_name} — ${engagement.name}`}
      >
        <span
          aria-hidden="true"
          className="block h-2 w-2 flex-none rounded-full"
          style={{ background: dotColor }}
        />
        <span className="truncate">{engagement.name}</span>
      </Link>
    );
  }

  // Active engagement — render the header row + nested sub-nav with a
  // left rule. Matches the prototype's expanded engagement-nav structure
  // (padding-left:18, border-left:1px ink-200, gap:1px, items at
  // height:28px / font-size:12px / `.active` background paper-0).
  return (
    <div className="flex flex-col">
      <Link
        to="/engagements/$engagementId"
        params={{ engagementId: engagement.id }}
        className={cn(
          'flex h-8 items-center gap-2 rounded-card border border-ink-100 bg-paper-0 px-2.5 text-body-sm font-semibold text-ink-900 no-underline',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        )}
        aria-current="page"
        title={`${engagement.client_name} — ${engagement.name}`}
      >
        <span
          aria-hidden="true"
          className="block h-2 w-2 flex-none rounded-full"
          style={{ background: dotColor }}
        />
        <span className="truncate">{engagement.name}</span>
      </Link>
      <div className="ml-2.5 mt-0.5 flex flex-col gap-px border-l border-ink-200 pl-3">
        {engagementSubItems(engagement.modules_enabled).map((item) => (
          <EngagementSubNavLink
            key={item.label}
            engagementId={engagement.id}
            label={item.label}
            tail={item.to}
            icon={item.icon}
            pathname={pathname}
          />
        ))}
      </div>
    </div>
  );
}

interface EngagementSubNavLinkProps {
  engagementId: string;
  label: string;
  tail: string;
  icon: typeof Home;
  pathname: string;
}

function EngagementSubNavLink({
  engagementId,
  label,
  tail,
  icon: Icon,
  pathname,
}: EngagementSubNavLinkProps) {
  const enginePrefix = `/engagements/${engagementId}`;
  const subPath = pathname.slice(enginePrefix.length).replace(/^\//, '');

  const active = (() => {
    if (tail === '') {
      // Overview matches the bare `/engagements/:id` (no tail) only.
      return subPath === '';
    }
    // For grouped routes (contract/baseline, discovery) we match on the
    // leading segment so deep links light up the correct module.
    const tailHead = tail.split('/')[0]!;
    return subPath === tail || subPath.startsWith(`${tailHead}/`) || subPath === tailHead;
  })();

  // Build the actual file-route path. The router expects the explicit
  // `/engagements/$engagementId/<tail>` shape; overview is just the bare
  // parent route.
  const to = tail === '' ? '/engagements/$engagementId' : `/engagements/$engagementId/${tail}`;

  return (
    <Link
      to={to}
      params={{ engagementId }}
      className={cn(
        'flex h-7 items-center gap-2 rounded-card px-2 text-meta no-underline transition-colors',
        'hover:bg-paper-200 hover:text-ink-900',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        active ? 'border border-ink-100 bg-paper-0 font-semibold text-ink-900' : 'text-ink-700',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <Icon
        className={cn('h-3.5 w-3.5 flex-none', active ? 'text-accent-amber-600' : 'text-ink-500')}
        aria-hidden="true"
      />
      <span className="truncate">{label}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// User card at the bottom of the sidebar. Mirrors the prototype's user
// block: avatar (palette tone from a hash of id+name), display name +
// "Role · Firm" line. Role isn't on the v1 identity envelope yet — falls
// back to "Member" until the auth payload surfaces one.
// ---------------------------------------------------------------------------

function UserCard() {
  const identity = useAuth((s) => s.identity);

  if (!identity) {
    return null;
  }

  const displayName = identity.displayName || identity.email || 'Dev Admin';
  const initials = deriveInitials(displayName);
  const tone = avatarTone({ id: identity.userId, name: displayName });
  // v1 identity envelope doesn't carry role — show firm name only.
  // Role lookup is a follow-up (the backend `users` endpoint exposes it
  // but the auth identity store stays minimal).
  const subline = FIRM_NAME;

  return (
    <div className="mt-3 flex items-center gap-2.5 border-t border-ink-100 px-2.5 pt-3">
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex h-7 w-7 flex-none items-center justify-center rounded-full border border-paper-0 font-mono text-[11px] font-semibold',
          `bg-avatar-${tone}-bg`,
          `text-avatar-${tone}-ink`,
        )}
      >
        {initials}
      </span>
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-meta font-medium text-ink-900">{displayName}</span>
        <span className="truncate text-[11px] text-ink-500">{subline}</span>
      </div>
    </div>
  );
}
